import {MAXIMO_POR_LOTE_DE_BIG_BAGS, type DetalleDeRevision} from '@cnc/contracts';
import {useState} from 'react';
import {casosDeLaRevision, useAceptarCasos, type VistaDeRevision} from '../api/calidad';
import {CambioDeFechaDeContenedores} from './cambio-de-fecha-de-contenedores';
import {useCatalogosDeSeguimiento} from '../api/seguimiento';
import {tr} from '../i18n/idioma';
import {numero} from '../utilidades/formato';
import {AYUDA_CALIDAD} from './ayuda-general';
import {NotaDeAyuda} from './nota-de-ayuda';
import {casoDeEnlace, type TipoDeCaso} from '../utilidades/vuelta';
import {CambioEnLote} from './cambio-en-lote';
import {CambioEnLoteDeBigBags, type CampoEnLote} from './cambio-en-lote-big-bags';

// Cambio masivo dentro de la revision de calidad (Daniel, 7/10/2026): se
// marcan los casos en la misma lista donde aparecen y se corrigen todos
// juntos con el cambio en lote de Big Bags o de exportaciones, que revisa
// antes de guardar. Lo corregido sale solo de la lista al refrescar.
// Los casos marcados tambien se pueden aceptar (decision 0040): dejan de
// contar sin cambiar el dato; en las incidencias, se marcan como decididas.

// El dato que normalmente se corrige en cada revision de Big Bags.
const CAMPO_POR_REVISION: Partial<Record<string, CampoEnLote>> = {
  sin_mes_molienda: 'mesMolienda',
  clasificacion_distinta: 'mina',
  historicos_sin_prefijo: 'mina',
};

// Casos marcados: clave del caso en la revision → enlace del registro.
export type CasosMarcados = Map<string, string | null>;

export function CambioMasivoDeRevision({
  detalle,
  tipo,
  seleccion,
  onSeleccion,
  vista,
}: {
  detalle: DetalleDeRevision;
  // Orden y filtros por columna puestos: "Marcar todos" marca solo los filtrados.
  vista: VistaDeRevision;
  // Tipo de registro de los casos, si todos son del mismo (para corregir en lote).
  tipo: TipoDeCaso | null;
  seleccion: CasosMarcados;
  onSeleccion: (casos: CasosMarcados) => void;
}) {
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loteAbierto, setLoteAbierto] = useState(false);
  const [aceptando, setAceptando] = useState(false);
  const [nota, setNota] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);
  const aceptar = useAceptarCasos(detalle.clave);
  const maximo = MAXIMO_POR_LOTE_DE_BIG_BAGS;
  // Las claves de los registros (BB # o exportacion) para corregir en lote.
  const registros = [
    ...new Set(
      [...seleccion.values()].flatMap(e => (tipo && casoDeEnlace(e)?.tipo === tipo ? [casoDeEnlace(e)!.clave] : [])),
    ),
  ];
  const decidir = detalle.accionDeAceptar === 'decidir';
  // En las revisiones de contenedores la clave del caso es "exportacion · contenedor".
  const contenedores =
    detalle.entidad === 'container'
      ? [...seleccion.keys()].flatMap(clave => {
          const [exportacion, contenedor] = clave.split(' · ');
          return exportacion && contenedor ? [{exportacion, contenedor}] : [];
        })
      : [];

  const seleccionarTodos = async () => {
    setCargando(true);
    setError(null);
    try {
      const casos = await casosDeLaRevision(detalle.clave, maximo, vista);
      onSeleccion(new Map(casos.map(c => [c.clave, c.enlace])));
    } catch {
      setError(tr('No se pudieron traer todos los casos.', 'Could not load all the cases.'));
    } finally {
      setCargando(false);
    }
  };

  const confirmarAceptar = () => {
    setError(null);
    aceptar.mutate(
      {claves: [...seleccion.keys()], nota: nota.trim() || undefined},
      {
        onSuccess: r => {
          setAviso(
            decidir
              ? tr(
                  `${numero(r.aceptados)} incidencias marcadas como decididas.`,
                  `${numero(r.aceptados)} issues marked as decided.`,
                )
              : tr(`${numero(r.aceptados)} casos aceptados.`, `${numero(r.aceptados)} cases accepted.`),
          );
          setAceptando(false);
          setNota('');
          onSeleccion(new Map());
        },
        onError: e => setError(e.message),
      },
    );
  };

  return (
    <div
      className="mt-3 flex flex-wrap items-center gap-2 rounded-md bg-papel px-3 py-2 text-xs"
      role="group"
      aria-label={tr('Cambio masivo de los casos', 'Bulk change of the cases')}
    >
      <span className="font-semibold text-apagado uppercase">
        {tipo ? tr('Corregir varios', 'Fix several') : tr('Varios a la vez', 'Several at once')}
      </span>
      <span>{tr(`${numero(seleccion.size)} marcados`, `${numero(seleccion.size)} ticked`)}</span>
      {seleccion.size < Math.min(detalle.cantidad, maximo) && (
        <button
          type="button"
          onClick={() => void seleccionarTodos()}
          disabled={cargando}
          className="text-verde hover:underline disabled:opacity-50"
        >
          {cargando
            ? tr('Trayendo…', 'Loading…')
            : tr(
                `Marcar los ${numero(Math.min(detalle.cantidad, maximo))} casos`,
                `Tick all ${numero(Math.min(detalle.cantidad, maximo))} cases`,
              )}
        </button>
      )}
      {seleccion.size > 0 && (
        <button type="button" onClick={() => onSeleccion(new Map())} className="text-apagado hover:text-tinta">
          {tr('Quitar selección', 'Clear selection')}
        </button>
      )}
      {error && <span className="text-alerta">{error}</span>}
      {aviso && <span className="text-verde-oscuro">{aviso}</span>}
      {tipo === 'bolsa' && (
        <>
          <span className="mx-1 text-linea">|</span>
          <CambioEnLoteDeBigBags
            bolsas={registros}
            campoInicial={CAMPO_POR_REVISION[detalle.clave]}
            onListo={() => onSeleccion(new Map())}
          />
        </>
      )}
      {detalle.entidad === 'container' && (
        <>
          <span className="mx-1 text-linea">|</span>
          <CambioDeFechaDeContenedores contenedores={contenedores} onListo={() => onSeleccion(new Map())} />
        </>
      )}
      {tipo === 'exportacion' && (
        <>
          <span className="mx-1 text-linea">|</span>
          <button
            type="button"
            disabled={!registros.length}
            onClick={() => setLoteAbierto(v => !v)}
            className="rounded-md border border-linea bg-white px-2 py-0.5 hover:bg-papel disabled:opacity-40"
          >
            ⇶ {tr('Cambiar en lote', 'Batch change')}
            {registros.length ? ` (${numero(registros.length)})` : ''}
          </button>
        </>
      )}
      <span className="mx-1 text-linea">|</span>
      <button
        type="button"
        disabled={!seleccion.size}
        onClick={() => {
          setAviso(null);
          setAceptando(v => !v);
        }}
        className="rounded-md border border-linea bg-white px-2 py-0.5 hover:bg-papel disabled:opacity-40"
      >
        ✓ {decidir ? tr('Marcar como decididas', 'Mark as decided') : tr('Aceptar', 'Accept')}
        {seleccion.size ? ` (${numero(seleccion.size)})` : ''}
      </button>
      {aceptando && seleccion.size > 0 && (
        <div className="flex basis-full flex-wrap items-center gap-2 rounded-md border border-linea bg-white px-3 py-2">
          <span>
            {decidir
              ? tr(
                  'Las incidencias de los grupos marcados quedan como decididas. El dato no cambia.',
                  'The issues of the ticked groups are marked as decided. The data does not change.',
                )
              : tr(
                  'Los casos marcados quedan aceptados: dejan de contar y de salir en la lista. El dato no cambia; si aparecen casos nuevos, siguen saliendo.',
                  'The ticked cases are accepted: they stop counting and showing in the list. The data does not change; new cases still show up.',
                )}
          </span>
          <input
            value={nota}
            onChange={e => setNota(e.target.value)}
            maxLength={500}
            placeholder={tr('Nota (opcional): por qué está bien', 'Note (optional): why it is fine')}
            aria-label={tr('Nota', 'Note')}
            className="min-w-56 flex-1 rounded-md border border-linea px-2 py-1"
          />
          <NotaDeAyuda titulo={tr('Nota', 'Note')} texto={AYUDA_CALIDAD.nota} />
          <button
            type="button"
            onClick={confirmarAceptar}
            disabled={aceptar.isPending}
            className="rounded-md bg-verde px-3 py-1 font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
          >
            {aceptar.isPending ? tr('Guardando…', 'Saving…') : tr('Confirmar', 'Confirm')}
          </button>
          <button type="button" onClick={() => setAceptando(false)} className="text-apagado hover:text-tinta">
            {tr('Cancelar', 'Cancel')}
          </button>
        </div>
      )}
      {loteAbierto && registros.length > 0 && (
        <div className="basis-full">
          {/* El resultado queda a la vista hasta cerrar; al cerrar se limpia la seleccion. */}
          <LoteDeExportaciones
            codigos={registros}
            onCerrar={() => {
              setLoteAbierto(false);
              onSeleccion(new Map());
            }}
            onAplicado={() => undefined}
          />
        </div>
      )}
    </div>
  );
}

function LoteDeExportaciones(props: {codigos: string[]; onCerrar: () => void; onAplicado: () => void}) {
  const catalogos = useCatalogosDeSeguimiento();
  return <CambioEnLote {...props} catalogos={catalogos.data} />;
}
